const NAME_TO_LABEL = {
  days: 'D',
  hours: 'H',
  minutes: 'M',
  seconds: 'S',
};

/**
 * Constructs the name for a timer cookie
 * @param {Banner} banner
 */
function getCookieName(banner) {
  return `countdown_start_${banner.name.replace(/ /g, '_')}`;
}

/**
 * Adds a cookie to the browser
 * @param {string} name The name of the cookie
 * @param {number | string} value The value of the cookie
 * @param {number} hours The duration of the cookie in hours
 */
function setCookie(name, value, hours) {
  let date = new Date();
  date.setTime(date.getTime() + hours * 60 * 60 * 1000);
  let expires = 'expires=' + date.toUTCString();
  document.cookie = name + '=' + value + ';' + expires + ';path=/';
}

/**
 * Retrieves the value of a cookie
 * @param {string} name
 * @returns {string | null} The value of the cookie if found; otherwise, null.
 */
function getCookie(name) {
  let nameEQ = name + '=';
  let ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

/**
 * Removes a cookie from the browser
 * @param {string} name
 */
function deleteCookie(name) {
  document.cookie = name + '=; Max-Age=-99999999;';
}

/**
 * Returns an object with the days, hours, minutes, and seconds from a duration in seconds
 * @param {number} totalSeconds The duration in seconds
 * @returns {Duration} The parsed duration
 */
function parseDuration(totalSeconds) {
  if (totalSeconds < 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  const days = Math.floor(totalSeconds / (60 * 60 * 24));
  const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  return { days, hours, minutes, seconds };
}

/**
 * Update the timer elements on the page
 * @param {Duration} parsedTime The parsed duration
 */
function updateTimerDisplay(parsedTime) {
  for (const key in parsedTime) {
    const value = parsedTime[key] < 10 ? '0' + parsedTime[key] : parsedTime[key];
    if (value === '00' && key === 'days') {
      document.querySelector('.announcement-bar__countdown-text.days').parentElement.style.display = 'none';
      document.querySelector('.announcement-bar__countdown-item:has(.days) + div').style.display = 'none';
    } else if (key === 'days') {
      document.querySelector('.announcement-bar__countdown-text.days').parentElement.style.display = 'flex';
      document.querySelector('.announcement-bar__countdown-item:has(.days) + div').style.display = 'block';
    }
    document.querySelector(`.announcement-bar__countdown-text.${key}`).textContent = value;
  }
}

/**
 * Starts the countdown interval to update the timer display every second
 * @param {number} totalSeconds The total duration in seconds
 * @param {string} cookieName The name of the cookie to store the start time
 */
function startCountdown(totalSeconds, cookieName) {
  let timer = totalSeconds;
  updateTimerDisplay(parseDuration(timer));

  const interval = setInterval(function () {
    updateTimerDisplay(parseDuration(timer));

    if (--timer < 0) {
      timer = 0;
      clearInterval(interval);
      deleteCookie(cookieName);
    }
  }, 1000);
}

/**
 * Function to find the appropriate banner to display based on the audience and current page
 * @param {Banner[]} banners The banners configured for the site
 * @param {boolean} isMember The membership status of the user
 * @param {boolean} isSubscriber The subscriber status of the user
 * @returns {Banner | null} The banner to display if found; otherwise, null.
 */
function findAppropriateBanner(banners, isMember, isSubscriber) {
  const now = new Date().getTime();
  const _banners = [];
  const hasMembership = isMember || isSubscriber;
  banners.forEach((banner) => {
    if (banner.start_date && new Date(banner.start_date).getTime() > now) {
      return;
    }
    if (banner.end_date && new Date(banner.end_date).getTime() < now) {
      return;
    }

    if (banner.display_on_page && banner.display_on_page !== window.location.pathname) {
      return;
    } else if (banner.display_on_page && banner.display_on_page === window.location.pathname) {
      // A banner configured to display on a specific page should take precedence over others
      // So we put it at the beginning of the array
      _banners.unshift(banner);
      return;
    }

    if (banner.customer_status === 'Only Members' && !hasMembership) {
      return;
    }

    if (banner.customer_status === 'Only Non-Members' && hasMembership) {
      return;
    }

    _banners.push(banner);
  });
  if (_banners.length) {
    return _banners[0];
  }
  return null;
}

/**
 * Adds a timer element (days/hours/minutes/seconds) to a given parent component
 * @param {'days' | 'hours' | 'minutes' | 'seconds'} name The name of the timer element
 * @param {Banner} banner The banner being displayed
 * @param {HTMLElement} parent The parent element to append the timer element
 * @param {boolean} [separator] Whether to add a separator after the timer element
 * @returns
 */
function buildTimeElement(name, banner, parent, separator = false) {
  const container = document.createElement('div');
  container.classList.add('announcement-bar__countdown-item');
  const textEl = document.createElement('p');
  textEl.classList.add(name);
  textEl.classList.add('announcement-bar__countdown-text');
  textEl.classList.add('font-eyebrow');
  textEl.style.color = banner.color.foreground;
  textEl.textContent = '00';
  container.appendChild(textEl);
  const labelEl = document.createElement('p');
  labelEl.classList.add('announcement-bar__countdown-label');
  labelEl.classList.add('uppercase');
  labelEl.classList.add('font-eyebrow');
  labelEl.style.color = banner.color.foreground;
  labelEl.textContent = NAME_TO_LABEL[name];
  container.appendChild(labelEl);
  parent.appendChild(container);
  if (separator) {
    const separatorEl = document.createElement('div');
    separatorEl.classList.add('font-eyebrow');
    separatorEl.textContent = ':';
    separatorEl.style.color = banner.color.foreground;
    parent.appendChild(separatorEl);
  }
  return container;
}

/**
 * Function to render a banner on the page
 * @param {Banner | null} banner
 * @param {HTMLElement} container
 * @param {HTMLElement} wrapper
 */
function renderSiteBanner(banner, container, wrapper) {
  if (!banner) {
    return;
  }
  wrapper.style.backgroundColor = banner.color.background;
  wrapper.style.color = banner.color.foreground;

  const bannerContent = document.createElement('div');
  bannerContent.classList.add('announcement-bar__content');
  const bannerText = document.createElement('p');
  bannerText.classList.add('announcement-bar__message');
  bannerText.classList.add('font-eyebrow');
  bannerText.classList.add('uppercase');
  bannerText.textContent = banner.content;
  bannerText.style.color = banner.color.foreground;
  bannerContent.appendChild(bannerText);
  container.appendChild(bannerContent);

  if (banner.countdown_enabled) {
    const countdownSeparator = document.createElement('div');
    countdownSeparator.classList.add('announcement-bar__countdown-separator');
    countdownSeparator.style.backgroundColor = banner.color.foreground;
    bannerContent.appendChild(countdownSeparator);
    const countdownContainer = document.createElement('div');
    countdownContainer.classList.add('announcement-bar__countdown');
    buildTimeElement('days', banner, countdownContainer, true);
    buildTimeElement('hours', banner, countdownContainer, true);
    buildTimeElement('minutes', banner, countdownContainer, true);
    buildTimeElement('seconds', banner, countdownContainer);
    bannerContent.appendChild(countdownContainer);

    let countdownDuration = 0;
    let timeElapsed = 0;
    const now = new Date().getTime();
    if (banner.countdown_hours === 0) {
      const endTime = new Date(banner.end_date).getTime();
      countdownDuration = (endTime - now) / 1000; // seconds remaining until end date
    } else {
      countdownDuration = 60 * 60 * banner.countdown_hours; // hours to seconds
      const startTime = getCookie(getCookieName(banner));

      if (!startTime || now - startTime >= countdownDuration * 1000) {
        setCookie(getCookieName(banner), now, 5);
      } else {
        timeElapsed = (now - startTime) / 1000; // time elapsed in seconds
        if (timeElapsed >= countdownDuration) {
          timeElapsed = countdownDuration;
          deleteCookie(getCookieName(banner));
        }
      }
    }
    startCountdown(countdownDuration - timeElapsed, getCookieName(banner));
  }
}

/**
 * On page load, render the appropriate banner
 */
window.addEventListener('DOMContentLoaded', () => {
  const announcementBarWrapper = document.getElementById('allstora-announcement-bar');
  const announcementBarInner = announcementBarWrapper.querySelector('.announcement-bar');
  const { bookclubCustomer, allstoraMember } = announcementBarWrapper.dataset;
  renderSiteBanner(
    findAppropriateBanner(window.Allstora.site_banners, allstoraMember === 'true', bookclubCustomer === 'true'),
    announcementBarInner,
    announcementBarWrapper
  );
});

/***********************************************
 ***********************************************
 * Helper Type Definitions
 ************************************************
 ***********************************************/

/**
 * @typedef {Object} Color
 * @property {string} background
 * @property {string} foreground
 * @property {string} name
 */

/**
 * @typedef {Object} Banner
 * @property {Color} color
 * @property {string} content
 * @property {boolean} countdown_enabled
 * @property {number} countdown_hours
 * @property {'All Customers' | 'Only Members' | 'Only Non-Members'} customer_status
 * @property {string | null} display_on_page
 * @property {string} end_date
 * @property {string} name
 * @property {string} start_date
 */

/**
 * @typedef {Object} Duration
 * @property {number} days
 * @property {number} hours
 * @property {number} minutes
 * @property {number} seconds
 */
